/**
 * The arithmetic behind the plan editor — what record each gesture writes,
 * which corners move together, what follows a moved wall, and how a change is
 * undone — kept apart from the components so every rule can be checked with
 * plain numbers (`plan-editor.test.ts`).
 *
 * The records are `@sts/geometry`'s `PlanEdit`s: one per element, stating what
 * the element now IS. So a gesture never "adds a step"; it writes the whole of
 * one or a few elements' records, and undoing it is writing back what those
 * records were before (`inverseBatch`).
 */

import {
  alongLineM,
  planEditKey,
  relocateOnWall,
  type OpeningAddData,
  type OpeningModifyData,
  type PlanEdit,
  type PlanEditPoint,
  type PlanEditTargetKind,
  type PlanLine,
  type PlanOpeningKind,
  type PlanRoom,
  type PlanSegment,
  type RoomModifyData,
  type WallAddData,
  type WallModifyData,
} from "@sts/geometry";
import { normaliseDegrees, type PlanWall } from "@sts/calc";
import type { PlacedGeometry } from "@sts/rules-data";
import type { PlanEditBatch, PlanObject } from "../api/types.js";
import { editDate, editOf, type HeldPlanEdit } from "./use-plan-edits.js";

/** Two wall ends this close are one corner: they move together. */
export const CORNER_JOIN_M = 0.03;

/** New openings, until the designer sizes them: an internal door, a window, a doorless opening. */
export const OPENING_DEFAULTS: Readonly<
  Record<PlanOpeningKind, { readonly widthM: number; readonly heightM: number; readonly sillHeightM: number }>
> = {
  door: { widthM: 0.8, heightM: 2.0, sillHeightM: 0 },
  window: { widthM: 1.2, heightM: 1.2, sillHeightM: 0.9 },
  opening: { widthM: 0.9, heightM: 2.0, sillHeightM: 0 },
};

export function recordFor(
  records: readonly HeldPlanEdit[],
  targetKind: PlanEditTargetKind,
  targetId: string,
): HeldPlanEdit | undefined {
  const key = planEditKey({ targetKind, targetId });
  return records.find((r) => planEditKey(r) === key);
}

export function lineOf(segment: Pick<PlanSegment, "start" | "end">): PlanLine {
  return { start: segment.start, end: segment.end };
}

/**
 * The record that makes wall `wall` run along `line`, keeping every other
 * correction already on it (its thickness, its height) — and, for a wall
 * added on the plan, keeping it an "add".
 */
export function wallRunEdit(records: readonly HeldPlanEdit[], wall: PlanSegment, storey: number, line: PlanLine): PlanEdit {
  const held = recordFor(records, "wall", wall.id);
  if (held?.action === "add") {
    return { targetKind: "wall", targetId: wall.id, action: "add", storey, data: { ...(held.data as WallAddData), start: line.start, end: line.end } };
  }
  const data: WallModifyData = { ...(held?.action === "modify" ? (held.data as WallModifyData) : {}), start: line.start, end: line.end };
  return { targetKind: "wall", targetId: wall.id, action: "modify", storey, data };
}

/** The record that sets wall `wall`'s thickness and/or height, keeping its run. */
export function wallPropsEdit(
  records: readonly HeldPlanEdit[],
  wall: PlanSegment,
  storey: number,
  patch: { readonly thicknessM?: number; readonly heightM?: number },
): PlanEdit {
  const held = recordFor(records, "wall", wall.id);
  if (held?.action === "add") {
    return { targetKind: "wall", targetId: wall.id, action: "add", storey, data: { ...(held.data as WallAddData), ...patch } };
  }
  return {
    targetKind: "wall",
    targetId: wall.id,
    action: "modify",
    storey,
    data: { ...(held?.action === "modify" ? (held.data as WallModifyData) : {}), ...patch },
  };
}

/** The record that slides or resizes opening `opening`, keeping its other corrections. */
export function openingEdit(
  records: readonly HeldPlanEdit[],
  opening: PlanSegment,
  storey: number,
  patch: OpeningModifyData,
): PlanEdit {
  const held = recordFor(records, "opening", opening.id);
  if (held?.action === "add") {
    const { wallId, ...rest } = patch;
    return {
      targetKind: "opening",
      targetId: opening.id,
      action: "add",
      storey,
      data: { ...(held.data as OpeningAddData), ...rest, ...(wallId ? { wallId } : {}) },
    };
  }
  return {
    targetKind: "opening",
    targetId: opening.id,
    action: "modify",
    storey,
    data: { ...(held?.action === "modify" ? (held.data as OpeningModifyData) : {}), ...patch },
  };
}

export function roomLabelEdit(roomRef: string, storey: number, label: string): PlanEdit {
  return { targetKind: "room", targetId: roomRef, action: "modify", storey, data: { label } };
}

/**
 * A room's correction with `patch` laid over what it already holds — so a
 * rename keeps the room's split or merge, and a split keeps its name. A
 * split and a merge never travel together (the API refuses both in one
 * record): setting one clears the other. Undefined when nothing is left.
 */
export function roomEdit(
  records: readonly HeldPlanEdit[],
  roomRef: string,
  storey: number,
  patch: { readonly label?: string | null; readonly split?: RoomModifyData["split"] | null; readonly mergeInto?: string | null },
): PlanEdit | undefined {
  const held = recordFor(records, "room", roomRef);
  const was = (held?.action === "modify" ? held.data : {}) as RoomModifyData;
  const pick = <K extends keyof RoomModifyData>(key: K): RoomModifyData[K] | undefined =>
    patch[key] === null ? undefined : (patch[key] as RoomModifyData[K] | undefined) ?? was[key];
  const label = pick("label");
  let split = pick("split");
  let mergeInto = pick("mergeInto");
  if (patch.split) mergeInto = undefined;
  if (patch.mergeInto) split = undefined;
  const data: { -readonly [K in keyof RoomModifyData]: RoomModifyData[K] } = {};
  if (label !== undefined) data.label = label;
  if (split !== undefined) data.split = split;
  if (mergeInto !== undefined) data.mergeInto = mergeInto;
  if (Object.keys(data).length === 0) return undefined;
  return { targetKind: "room", targetId: roomRef, action: "modify", storey, data };
}

/**
 * Taking an element off the plan. A wall or opening added on the plan simply
 * has its record reverted; a captured one gets a "delete" record. A wall's own
 * openings go with it (`applyPlanEdits` removes them), so their records —
 * which could no longer apply — are reverted in the same change; undo puts
 * them all back.
 */
export function deleteBatch(
  records: readonly HeldPlanEdit[],
  kind: "wall" | "opening",
  id: string,
  storey: number,
  hostedOpeningIds: readonly string[] = [],
): PlanEditBatch {
  const held = recordFor(records, kind, id);
  const revert: { targetKind: PlanEditTargetKind; targetId: string }[] = [];
  const upsert: PlanEdit[] = [];
  if (held?.action === "add") revert.push({ targetKind: kind, targetId: id });
  else upsert.push({ targetKind: kind, targetId: id, action: "delete", storey });
  if (kind === "wall") {
    const hosted = new Set(hostedOpeningIds);
    for (const r of records) {
      if (r.targetKind !== "opening" || r.action === "delete") continue;
      const onThis = hosted.has(r.targetId) || (r.action === "add" && (r.data as OpeningAddData).wallId === id);
      if (onThis) revert.push({ targetKind: "opening", targetId: r.targetId });
    }
  }
  return { upsert, revert };
}

/** Undoing `batch`: every record it touched, back as it was in `records` (or gone, if it was not there). */
export function inverseBatch(records: readonly HeldPlanEdit[], batch: PlanEditBatch): PlanEditBatch {
  const upsert: PlanEdit[] = [];
  const revert: { targetKind: PlanEditTargetKind; targetId: string }[] = [];
  const upserted = new Set(batch.upsert.map(planEditKey));
  const seen = new Set<string>();
  for (const t of [...batch.upsert, ...batch.revert]) {
    const key = planEditKey(t);
    if (seen.has(key)) continue;
    seen.add(key);
    const before = recordFor(records, t.targetKind, t.targetId);
    if (before) upsert.push(editOf(before));
    // Not there before: undoing means taking away what the change wrote. (A
    // revert of something that was not there changed nothing to undo.)
    else if (upserted.has(key)) revert.push({ targetKind: t.targetKind, targetId: t.targetId });
  }
  return { upsert, revert };
}

/** True when a batch would change nothing at all. */
export function isEmptyBatch(batch: PlanEditBatch): boolean {
  return batch.upsert.length === 0 && batch.revert.length === 0;
}

// ------------------------------------------------------------ corners ----

export interface WallEnd {
  readonly wallId: string;
  readonly which: "start" | "end";
}

/** Every other wall end sitting on `point` — the corners that move with it. */
export function endsAt(
  walls: readonly PlanSegment[],
  point: PlanEditPoint,
  exclude: ReadonlySet<string>,
  toleranceM = CORNER_JOIN_M,
): WallEnd[] {
  const out: WallEnd[] = [];
  for (const wall of walls) {
    if (exclude.has(wall.id)) continue;
    if (Math.hypot(wall.start.x - point.x, wall.start.y - point.y) <= toleranceM) out.push({ wallId: wall.id, which: "start" });
    else if (Math.hypot(wall.end.x - point.x, wall.end.y - point.y) <= toleranceM) out.push({ wallId: wall.id, which: "end" });
  }
  return out;
}

/**
 * New runs for the walls whose ends move: `moves` says where each moving end
 * goes; any other wall end that was on the same corner goes with it (unless
 * `detached`). Returns each changed wall's line before and after.
 */
export function movedWalls(
  walls: readonly PlanSegment[],
  moves: readonly { readonly wallId: string; readonly which: "start" | "end"; readonly to: PlanEditPoint }[],
  detached = false,
): Map<string, { readonly before: PlanLine; readonly after: PlanLine }> {
  const byId = new Map(walls.map((w) => [w.id, w] as const));
  const next = new Map<string, { start: PlanEditPoint; end: PlanEditPoint }>();
  const moving = new Set(moves.map((m) => m.wallId));
  const set = (wallId: string, which: "start" | "end", to: PlanEditPoint): void => {
    const wall = byId.get(wallId);
    if (!wall) return;
    const line = next.get(wallId) ?? { start: wall.start, end: wall.end };
    next.set(wallId, which === "start" ? { ...line, start: to } : { ...line, end: to });
  };
  for (const move of moves) {
    const wall = byId.get(move.wallId);
    if (!wall) continue;
    const from = move.which === "start" ? wall.start : wall.end;
    set(move.wallId, move.which, move.to);
    if (!detached) for (const end of endsAt(walls, from, moving)) set(end.wallId, end.which, move.to);
  }
  const out = new Map<string, { before: PlanLine; after: PlanLine }>();
  for (const [id, after] of next) {
    const wall = byId.get(id) as PlanSegment;
    const unchanged =
      Math.hypot(after.start.x - wall.start.x, after.start.y - wall.start.y) < 1e-6 &&
      Math.hypot(after.end.x - wall.end.x, after.end.y - wall.end.y) < 1e-6;
    if (!unchanged) out.set(id, { before: lineOf(wall), after });
  }
  return out;
}

// ------------------------------------------------------- objects follow ----

const PARALLEL_SIN = Math.sin((5 * Math.PI) / 180);
/** A radiator this far off its seat still counts as on the wall. */
const RADIATOR_SEAT_SLACK_M = 0.05;
/** An outdoor unit backed to a wall within this of its face goes with the wall. */
const UNIT_REACH_M = 1.0;

type Footprinted = Extract<PlanObject, { kind: "radiator" | "heat-pump" }>;

function isFootprinted(object: PlanObject): object is Footprinted {
  return object.kind === "radiator" || object.kind === "heat-pump";
}

/**
 * Is `object` against `wall`? A radiator: parallel to it, its back on the
 * room-side face (within 50 mm), and within the wall's length. An outdoor
 * unit: parallel, on the outside, within a metre of the face.
 */
export function isOnWall(object: PlanObject, wall: PlanWall): boolean {
  if (!isFootprinted(object)) return false;
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const length = Math.hypot(dx, dy);
  if (!(length > 1e-6)) return false;
  const a = { x: dx / length, y: dy / length };
  const t = (object.geometry.rotationDeg * Math.PI) / 180;
  const u = { x: Math.cos(t), y: Math.sin(t) };
  if (Math.abs(u.x * a.y - u.y * a.x) > PARALLEL_SIN) return false;
  const rel = { x: object.geometry.x - wall.start.x, y: object.geometry.y - wall.start.y };
  const along = rel.x * a.x + rel.y * a.y;
  if (along < -0.05 || along > length + 0.05) return false;
  const side = rel.x * wall.inward.x + rel.y * wall.inward.y;
  if (object.kind === "radiator") {
    const halfDepth = object.spec.depthMm / 2000;
    return side > 0 && Math.abs(side - halfDepth) <= RADIATOR_SEAT_SLACK_M;
  }
  const halfDepth = object.spec.depthMm / 2000;
  return side < 0 && -side <= halfDepth + UNIT_REACH_M;
}

export interface ObjectFollow {
  readonly id: string;
  readonly before: PlacedGeometry;
  readonly after: PlacedGeometry;
}

/**
 * The radiators and outdoor units on walls that moved, moved with them: kept
 * the same distance along from whichever end of the wall stayed put (the rule
 * the wall's own windows follow), the same distance off its face, turned as
 * it turned — and a radiator kept within the wall's length. `orphaned` lists
 * those whose wall was deleted: they stay where they are, and the plan says so.
 */
export function followWalls(
  objects: readonly PlanObject[],
  walls: readonly PlanWall[],
  moved: ReadonlyMap<string, { readonly before: PlanLine; readonly after: PlanLine }>,
  deleted: ReadonlySet<string> = new Set(),
): { follows: ObjectFollow[]; orphaned: string[] } {
  const follows: ObjectFollow[] = [];
  const orphaned: string[] = [];
  for (const object of objects) {
    if (!isFootprinted(object)) continue;
    const wall = walls.find((w) => (moved.has(w.id) || deleted.has(w.id)) && isOnWall(object, w));
    if (!wall) continue;
    if (deleted.has(wall.id)) {
      orphaned.push(object.id);
      continue;
    }
    const change = moved.get(wall.id) as { before: PlanLine; after: PlanLine };
    const half = object.kind === "radiator" ? object.spec.lengthMm / 2000 : 0;
    const to = relocateOnWall({ x: object.geometry.x, y: object.geometry.y }, change.before, change.after, half);
    follows.push({
      id: object.id,
      before: object.geometry,
      after: {
        x: to.point.x,
        y: to.point.y,
        rotationDeg: normaliseDegrees(object.geometry.rotationDeg + to.turnDeg),
      },
    });
  }
  return { follows, orphaned };
}

// ------------------------------------------------------------ openings ----

/** Along its host wall, where an opening's centre is, metres from the wall's start. */
export function openingOffsetM(opening: PlanSegment, host: PlanSegment): number {
  const centre = { x: (opening.start.x + opening.end.x) / 2, y: (opening.start.y + opening.end.y) / 2 };
  return alongLineM(centre, lineOf(host));
}

/** An opening's sill: its bottom edge above the bottom of its wall, when both are known. */
export function openingSillM(opening: PlanSegment, host: PlanSegment | undefined): number | undefined {
  if (!opening.vertical || !host?.vertical) return undefined;
  return opening.vertical.bottomY - host.vertical.bottomY;
}

/** Which room a point is in — the one whose floor contains it, else the one whose centre is nearest. */
export function roomAt(rooms: readonly PlanRoom[], point: PlanEditPoint): PlanRoom | undefined {
  const inside = (poly: readonly PlanEditPoint[]): boolean => {
    let hit = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const a = poly[i] as PlanEditPoint;
      const b = poly[j] as PlanEditPoint;
      if (a.y > point.y !== b.y > point.y && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) hit = !hit;
    }
    return hit;
  };
  const containing = rooms.find((room) => room.outlines.some(inside));
  if (containing) return containing;
  let best: PlanRoom | undefined;
  let bestD = Infinity;
  for (const room of rooms) {
    const d = Math.hypot(room.centre.x - point.x, room.centre.y - point.y);
    if (d < bestD) {
      bestD = d;
      best = room;
    }
  }
  return best;
}

/** "Edited by Dara Designer on 11 Sep 2026" — or "Saving…" while it is on its way. */
export function editedByLine(record: HeldPlanEdit | undefined): string | undefined {
  if (!record) return undefined;
  if (record.pending) return "Saving…";
  const who = record.updatedBy?.name || record.createdBy?.name || "someone";
  const date = editDate(record.updatedAt) ?? record.updatedAt;
  const verb = record.action === "add" ? "Added" : record.action === "delete" ? "Deleted" : "Edited";
  return `${verb} by ${who} on ${date}`;
}
